import { useEffect, useRef } from 'react';
import { addPainter, CHALK, curve, dust, ln, reducedMotion, ring, seg, wave, type ChalkColor, type Pt } from './chalk';

export type MarkKind = 'underline' | 'rule' | 'ring' | 'box' | 'tick' | 'wave' | 'dot' | 'speaker' | 'ghost';

interface Props {
  kind: MarkKind;
  color?: ChalkColor;
  delay?: number; // seconds after the mark comes into view
  dur?: number;
  width?: number;
  pad?: number; // room around the element the mark may spill into
  seed?: number;
  alpha?: number;
  replay?: unknown; // change it to draw the mark again (a hover redraw)
  className?: string;
}

// A chalk mark drawn over its parent (which must be position: relative). Draws in when it scrolls into view,
// shivering as the chalk moves, then holds still: on a screen read up close, settled marks don't move.
// Under reduced motion it is simply there.
export function ChalkMark({ kind, color = 'chalk', delay = 0, dur = 0.6, width = 2.4, pad = 10, seed = 1, alpha = 1, replay, className }: Props) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    const g = c?.getContext('2d');
    if (!c || !g) return;
    const R = Math.min(window.devicePixelRatio || 1, 2);
    let start: number | null = null;
    let W = 0;
    let H = 0;
    let lastBoil = 0;
    let settled = false;
    const io = new IntersectionObserver((es) => {
      if (es.some((e) => e.isIntersecting) && start === null) start = performance.now();
    });
    io.observe(c);
    const off = addPainter((now, boil) => {
      const w0 = c.clientWidth;
      const h0 = c.clientHeight;
      let resized = false;
      if (w0 !== W || h0 !== H) {
        W = w0;
        H = h0;
        c.width = Math.round(W * R);
        c.height = Math.round(H * R);
        resized = true;
      }
      if (start === null || !W || !H) return;
      const f = reducedMotion() ? 1 : seg((now - start) / 1000, delay, delay + dur);
      if (settled && !resized) return;
      if (f <= 0 && !resized) return;
      if (f < 1) lastBoil = boil; // the line freezes on the frame it finished on
      settled = f >= 1;
      g.setTransform(R, 0, 0, R, 0, 0);
      g.clearRect(0, 0, W, H);
      draw(g, kind, W, H, pad, f, seed, kind === 'ghost' ? 0 : lastBoil, width, CHALK[color], alpha);
      dust(g, W, H, 0.28);
    });
    return () => {
      off();
      io.disconnect();
    };
  }, [kind, color, delay, dur, width, pad, seed, alpha, replay]);
  return (
    <canvas
      ref={ref}
      className={`chalk-mark${className ? ` ${className}` : ''}`}
      style={{ left: -pad, top: -pad, width: `calc(100% + ${2 * pad}px)`, height: `calc(100% + ${2 * pad}px)` }}
      aria-hidden="true"
    />
  );
}

function draw(g: CanvasRenderingContext2D, kind: MarkKind, W: number, H: number, pad: number, f: number, seed: number, boil: number, w: number, col: string, a: number) {
  const x0 = pad;
  const y0 = pad;
  const x1 = W - pad;
  const y1 = H - pad;
  const bw = x1 - x0;
  const bh = y1 - y0;
  // a shape made of several strokes draws them one after another
  const strokes = (list: Pt[][], c = col, lw = w) =>
    list.forEach((p, i) => ln(g, p, c, seg(f, i / list.length, (i + 1) / list.length), seed + i * 3, boil, lw, a));

  switch (kind) {
    case 'underline':
      strokes([curve(x0 - 2, y1 + pad * 0.35, x1 + 4, y1 + pad * 0.3, -2)]);
      break;
    case 'rule':
      strokes([[[x0, y1 + pad * 0.3], [x1, y1 + pad * 0.3 - 1]]]);
      break;
    case 'wave':
      strokes([wave(x0, x1, y1 + pad * 0.35, 3, 18)]);
      break;
    case 'ring':
      strokes([ring((x0 + x1) / 2, (y0 + y1) / 2, bw / 2 + pad * 0.45, bh / 2 + pad * 0.45, seed)]);
      break;
    case 'box':
      // four strokes that overshoot at the corners, as a box drawn by hand does
      strokes([
        [[x0 - 5, y0 + 1], [x1 + 4, y0 - 1]],
        [[x1, y0 - 5], [x1 + 1, y1 + 4]],
        [[x1 + 5, y1], [x0 - 4, y1 + 1]],
        [[x0, y1 + 5], [x0 - 1, y0 - 4]],
      ]);
      break;
    case 'tick':
      strokes([[[x0 + bw * 0.12, y0 + bh * 0.55], [x0 + bw * 0.4, y1 - bh * 0.08], [x1 - bw * 0.06, y0 + bh * 0.08]]]);
      break;
    case 'dot': {
      const r = Math.min(bw, bh) / 2;
      ln(g, ring((x0 + x1) / 2, (y0 + y1) / 2, r * 0.5, r * 0.5, seed), col, f, seed, boil, r, a);
      break;
    }
    case 'speaker': {
      // a person talking: head, body, an arm out, and a speech bubble with three lines in it
      const s = bh * 0.42;
      const px = x0 + bw * 0.22;
      const py = y1 - 2;
      strokes([
        ring(px, py - 1.55 * s, 0.19 * s, 0.21 * s, seed),
        [[px, py - 1.33 * s], [px, py - 0.62 * s]],
        [[px - 0.24 * s, py], [px, py - 0.62 * s], [px + 0.24 * s, py]],
        [[px - 0.3 * s, py - 0.78 * s], [px, py - 1.18 * s], [px + 0.4 * s, py - 0.95 * s]],
        [[x0 + bw * 0.42, y0 + bh * 0.12], [x1 - 4, y0 + bh * 0.1], [x1 - 2, y0 + bh * 0.52], [x0 + bw * 0.56, y0 + bh * 0.54], [x0 + bw * 0.44, y0 + bh * 0.68], [x0 + bw * 0.47, y0 + bh * 0.52], [x0 + bw * 0.42, y0 + bh * 0.5], [x0 + bw * 0.42, y0 + bh * 0.12]],
        [[x0 + bw * 0.5, y0 + bh * 0.22], [x1 - bw * 0.1, y0 + bh * 0.22]],
        [[x0 + bw * 0.5, y0 + bh * 0.32], [x1 - bw * 0.16, y0 + bh * 0.32]],
        [[x0 + bw * 0.5, y0 + bh * 0.42], [x1 - bw * 0.26, y0 + bh * 0.42]],
      ]);
      break;
    }
    case 'ghost': {
      // last lesson's marks, rubbed out: a chart grid, a ring and an arrow. Drawings only, never words.
      const gx = x0 + bw * 0.5;
      const gy = y0 + bh * 0.12;
      const cw = bw * 0.14;
      const rh = bh * 0.2;
      const grid: Pt[][] = [];
      for (let r = 0; r <= 3; r++) grid.push([[gx, gy + r * rh], [gx + cw * 3, gy + r * rh]]);
      for (let k = 0; k <= 3; k++) grid.push([[gx + k * cw, gy - 6], [gx + k * cw, gy + 3 * rh + 4]]);
      grid.forEach((p, i) => ln(g, p, col, 1, seed + i * 7, 0, 5, a));
      ln(g, ring(gx + cw * 1.5, gy + rh * 1.5, cw * 0.32, rh * 0.32, seed + 40), col, 1, seed + 40, 0, 5, a);
      ln(g, curve(x0 + bw * 0.08, y0 + bh * 0.82, gx - 20, gy + rh * 2.2, -40), col, 1, seed + 50, 0, 5, a);
      ln(g, [[x0 + bw * 0.06, y0 + bh * 0.95], [x0 + bw * 0.36, y0 + bh * 0.96]], col, 1, seed + 60, 0, 4, a);
      break;
    }
  }
}
